import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import './css/Inventory.css';

/** Show search UI only when option count is at least this (matches ingredients dropdown pattern). */
export const SEARCHABLE_SELECT_MIN_OPTIONS = 6;

/** Must sit above `.menu-item-modal-overlay` (2100) and default inventory modals (1200). */
const DROPDOWN_Z_INDEX = 2200;

/**
 * Searchable dropdown (ingredients-style search + filtered option list).
 * Dropdown renders in a portal so it is not clipped by modal overflow.
 * @param {{ value: string|number, label: string, hint?: string }[]} options
 */
export default function SearchableSelect({
    value,
    onChange,
    options = [],
    placeholder = 'Select…',
    searchPlaceholder = 'Search…',
    loading = false,
    loadingLabel = 'Loading…',
    disabled = false,
    emptyMessage = 'No results found',
    className = '',
    id,
    triggerClassName = 'add-menu-form-field',
    minOptionsForSearch = SEARCHABLE_SELECT_MIN_OPTIONS,
    usePortal = true,
    dropdownMinWidth = 280,
}) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [dropdownStyle, setDropdownStyle] = useState(null);
    const rootRef = useRef(null);
    const triggerRef = useRef(null);
    const dropdownRef = useRef(null);

    const selected = useMemo(
        () => options.find((o) => String(o.value) === String(value)),
        [options, value]
    );

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return options;
        return options.filter(
            (o) =>
                String(o.label ?? '')
                    .toLowerCase()
                    .includes(q) ||
                String(o.hint ?? '')
                    .toLowerCase()
                    .includes(q)
        );
    }, [options, search]);

    const updateDropdownPosition = useCallback(() => {
        const el = triggerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const gap = 6;
        const maxHeight = 280;
        const spaceBelow = window.innerHeight - rect.bottom - gap;
        const spaceAbove = rect.top - gap;
        const openAbove = spaceBelow < 160 && spaceAbove > spaceBelow;
        const height = Math.min(maxHeight, openAbove ? spaceAbove : spaceBelow);

        const minW = Number(dropdownMinWidth) > 0 ? Number(dropdownMinWidth) : 0;
        const panelWidth = Math.max(rect.width, minW);

        setDropdownStyle({
            position: 'fixed',
            left: rect.left,
            width: panelWidth,
            minWidth: minW > 0 ? minW : undefined,
            zIndex: DROPDOWN_Z_INDEX,
            maxHeight: height > 80 ? height : maxHeight,
            ...(openAbove
                ? { bottom: window.innerHeight - rect.top + gap }
                : { top: rect.bottom + gap }),
        });
    }, [dropdownMinWidth]);

    useLayoutEffect(() => {
        if (!open || !usePortal) {
            setDropdownStyle(null);
            return undefined;
        }
        updateDropdownPosition();
        const onScrollOrResize = () => updateDropdownPosition();
        window.addEventListener('scroll', onScrollOrResize, true);
        window.addEventListener('resize', onScrollOrResize);
        return () => {
            window.removeEventListener('scroll', onScrollOrResize, true);
            window.removeEventListener('resize', onScrollOrResize);
        };
    }, [open, usePortal, updateDropdownPosition]);

    useEffect(() => {
        if (!open) return undefined;
        const onPointerDown = (e) => {
            const target = e.target;
            if (
                rootRef.current?.contains(target) ||
                dropdownRef.current?.contains(target)
            ) {
                return;
            }
            setOpen(false);
            setSearch('');
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [open]);

    const pick = (opt) => {
        onChange(String(opt.value));
        setOpen(false);
        setSearch('');
    };

    const toggle = () => {
        if (disabled || loading) return;
        setOpen((prev) => {
            if (prev) setSearch('');
            return !prev;
        });
    };

    const showSearch =
        Number(minOptionsForSearch) > 0 && options.length >= Number(minOptionsForSearch);

    const dropdownPanel = (
        <div
            ref={dropdownRef}
            className={`inventory-select-dropdown${
                usePortal ? ' inventory-select-dropdown--portal' : ''
            }`}
            style={usePortal ? dropdownStyle ?? undefined : undefined}
            role="presentation"
            onMouseDown={(e) => e.stopPropagation()}
        >
            {showSearch && (
                <div className="ingredients-search-wrap searchable-select-search">
                    <input
                        type="text"
                        className="ingredients-search-input"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={searchPlaceholder}
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                    />
                    {search.trim() !== '' && (
                        <button
                            type="button"
                            className="ingredients-search-clear"
                            aria-label="Clear search"
                            onClick={() => setSearch('')}
                        >
                            <FontAwesomeIcon icon={faXmark} />
                        </button>
                    )}
                </div>
            )}
            <div className="inventory-options-list" role="listbox">
                {filtered.length > 0 ? (
                    filtered.map((opt) => (
                        <button
                            key={String(opt.value)}
                            type="button"
                            role="option"
                            aria-selected={String(opt.value) === String(value)}
                            className="inventory-option-item"
                            onClick={() => pick(opt)}
                        >
                            <span>{opt.label}</span>
                            {opt.hint ? (
                                <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                    {opt.hint}
                                </span>
                            ) : null}
                        </button>
                    ))
                ) : (
                    <div className="inventory-option-empty">{emptyMessage}</div>
                )}
            </div>
        </div>
    );

    const showDropdown = open && (!usePortal || dropdownStyle);

    return (
        <div
            className={`inventory-searchable-select searchable-select ${className}`.trim()}
            ref={rootRef}
            id={id}
        >
            <button
                ref={triggerRef}
                type="button"
                className={`${triggerClassName} inventory-select-trigger${
                    open ? ' inventory-select-trigger--open' : ''
                }`}
                onClick={toggle}
                disabled={disabled || loading}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                {loading ? loadingLabel : selected?.label || placeholder}
            </button>
            {showDropdown &&
                (usePortal && typeof document !== 'undefined'
                    ? createPortal(dropdownPanel, document.body)
                    : dropdownPanel)}
        </div>
    );
}
